import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tooltip } from '@/components/ui/tooltip';
import { AdminDataService } from '@/services/admin-data';
import { 
  getStatusIcon, 
  getStatusBadgeColor, 
  getStatusTooltip, 
  formatUserInitials,
  sortUsersByProperty 
} from '@/utils/admin-helpers';
import type { AdminUser } from '@/types/admin';

export function AppAdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Set initial filter from URL params
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setStatusFilter(filterParam);
    }
  }, [searchParams]);

  // Get data from service
  const adminService = AdminDataService.getInstance();
  const users: AdminUser[] = adminService.getUsers();

  // Get filtered users based on filter type
  let filteredUsers: AdminUser[];
  
  switch (statusFilter) {
    case 'all':
      filteredUsers = users;
      break;
    case 'active':
      filteredUsers = adminService.getActiveUsers();
      break;
    case 'repeat':
      filteredUsers = adminService.getRepeatUsers();
      break;
    case 'thriving':
    case 'managing':
    case 'struggling':
    case 'inactive':
      filteredUsers = users.filter(user => user.status.toLowerCase() === statusFilter.toLowerCase());
      break;
    default:
      filteredUsers = users;
  }

  // Sort by number of sessions (descending)
  const sortedUsers = sortUsersByProperty(filteredUsers, 'sessions', 'desc');


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
            <p className="text-sm text-gray-600 mt-1">Manage and monitor user engagement and coaching progress</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
              WM
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Wei Ming</p>
              <p className="text-xs text-gray-500">Octave Institute</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-8 py-8">

        {/* Users Table */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
        >
          <div className="px-6 py-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">User Management</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {sortedUsers.length} of {users.length} users • Sorted by coaching sessions (highest first)
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      const newFilter = e.target.value;
                      setStatusFilter(newFilter);
                      // Update URL params
                      if (newFilter === 'all') {
                        setSearchParams({});
                      } else {
                        setSearchParams({ filter: newFilter });
                      }
                    }}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  >
                    <option value="all">All Users ({users.length})</option>
                    <option value="active">Active Users ({adminService.getActiveUsers().length})</option>
                    <option value="repeat">Repeat Users ({adminService.getRepeatUsers().length})</option>
                    <option value="thriving">Thriving ({users.filter(u => u.status === 'Thriving').length})</option>
                    <option value="managing">Managing ({users.filter(u => u.status === 'Managing').length})</option>
                    <option value="struggling">Struggling ({users.filter(u => u.status === 'Struggling').length})</option>
                    <option value="inactive">Inactive ({users.filter(u => u.status === 'Inactive').length})</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {statusFilter !== 'all' && (
                  <button 
                    onClick={() => setStatusFilter('all')}
                    className="px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Avg Duration
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedUsers.map((user, index) => (
                  <motion.tr 
                    key={user.id} 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="hover:bg-gray-50/70 transition-all duration-200 group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-sm font-bold text-white">
                            {formatUserInitials(user.name)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">ID: {user.id.padStart(3, '0')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-lg font-bold text-gray-900">{user.sessions}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">{user.averageSessionLength}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{user.lastSession}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Tooltip content={getStatusTooltip(user.status)} position="top">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border cursor-help transition-all group-hover:scale-105 ${getStatusBadgeColor(user.status)}`}>
                          {(() => {
                            const IconComponent = getStatusIcon(user.status);
                            return <IconComponent className="w-4 h-4 mr-1.5" />;
                          })()}
                          {user.status}
                        </span>
                      </Tooltip>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30 flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <span>Rows per page:</span>
              <select className="border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary">
                <option>15</option>
                <option>25</option>
                <option>50</option>
              </select>
            </div>
            <div className="flex items-center space-x-4">
              <span>Showing {sortedUsers.length} of {users.length} users</span>
              <div className="flex items-center space-x-2">
                <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
}